import { useEffect, useMemo, useRef } from 'react';
import { COMPONENT_FORM_CONFIGS } from '../componentFormConfigs';
import { useComponentForm } from '../useComponentForm';
import { FormSubtab, handleTabToNext, InputRow } from '../formKit';
import {
  AirStreamFlow,
  BlankOffSlot,
  FormHeading,
  HeadingChrome,
  MBareInput,
  MBareSelect,
  PBareSelect,
  PerformanceCommon,
  PricingRows,
  PSelect,
  RequiredFieldNote,
} from './formCommon';
import type { ComponentFormProps } from './ComponentFormHost';
import type { FormModel } from '../autoSaveModel';

/**
 * Phase 6e2a — FILTER form, re-cut byte-faithful to
 * components/filter/tmpl/form.tmpl (verify round 1): subtab census
 * properties/performance/frame/pricing (NO remarks), MERV | Efficiency +
 * Supplied By | Sets pairs, "Bank Configuration W x H" size row, non-clickable
 * sublists, hidden smartlist chrome (`ahu-header-btns right hide`) + visible
 * Rating block, EDB/LDB temp rows hidden (li.hide).
 *
 * Cross-filter web (filterIndex 1, §50.1 / parity 320) unchanged.
 */
const config = COMPONENT_FORM_CONFIGS.filter;

/** Per-catalogue-engine slice of the §49.2 render freeze (see FilterForm). */
interface EngineSnapshot {
  params: Record<string, unknown>;
  cmsCount: number | undefined;
  cmsFullCount: number | undefined;
}

const FilterForm = ({ row, section, section2, cms }: ComponentFormProps) => {
  const formApi = useComponentForm({
    config,
    row,
    section,
    section2,
    firstSubtab: 'properties',
  });
  const liveModel = formApi.model;

  // ---- §49.2 render gate (component_view.js:687) ----
  // `if (!self.rate && self.template && ...) self.$el.html(self.template(data))`
  // — while a selection dialog owns this component (view.rate === true) legacy
  // NEVER repaints the Data Bar form, even though `filter.selector.js` is
  // editing that very same model in place on every keystroke (js:610). The port
  // carried the sibling autosave guard (component_model.js:79 →
  // autoSaveModel.ts `viewRef.rate`) but not this one, so every edit in the
  // dialog reached Data Bar → Component instantly. Legacy repaints once, from
  // selectFilter's explicit `filterView.rate = false; filterView.render()`
  // (js:738,:798) — shim.rate/shim.render() here, via restoreSeam().
  //
  // Freeze the VALUES, not the element. Caching the rendered element also
  // freezes the form's own interaction, and legacy keeps that live: the
  // Performance/Frame/Pricing collapses are native `input[data-subtab]`
  // checkboxes shown by CSS `:checked`, and `subtabOpenClose`
  // (component_view.js:1172-1184) only records the flag for the NEXT render —
  // it paints nothing, so the collapses work perfectly well while the DOM is
  // frozen. Rendering live structure off a frozen attribute snapshot
  // reproduces both halves; every value this form paints comes from
  // `model.get()` (it reads no `selectedModel`/`cmsPages`, and its catalogue
  // chrome is the hidden `ahu-header-btns right hide` variant), so the
  // snapshot is a complete freeze of what the user can see.
  //
  // TWO sources, not one. The model carries most of it, but the Manufacturer /
  // Type / Series / MERV / Efficiency / Nominal Depth rows (and the Frame tab's
  // four) are <PSelect>/<PBareSelect>, which read `engine.paramValue(name)` —
  // i.e. the catalogue engine's `filters[0]`, NOT the model
  // (formCommon.tsx:399,:446). The dialog's `onParamsField` writes that very
  // engine through the shim (`resetFilter`), so those selects mirrored the
  // dialog live no matter what the model did. Same for the Frame chrome's
  // `cmsCount`/`cmsFullCount` (formCommon.tsx:62-70). Freeze both sources.
  //
  // Snapshot on every UNFROZEN paint, and hold it for the dialog's lifetime.
  // Deliberately not "snapshot when rate flips": `rate` is a mutable ref, so
  // flipping it re-renders nothing, and the next paint is triggered BY the
  // selector's first write — snapshotting there would bake that write in. What
  // legacy's frozen DOM holds is the data of the last render it was allowed to
  // do, and this is that, exactly.
  const lastPaintedAttrs = useRef<Record<string, unknown> | null>(null);
  const lastPaintedFields = useRef<Record<string, EngineSnapshot> | null>(null);
  if (!formApi.rate) {
    if (liveModel) lastPaintedAttrs.current = liveModel.toJSON();
    const fields: Record<string, EngineSnapshot> = {};
    Object.keys(formApi.catalogueFields).forEach((target) => {
      const engine = formApi.catalogueFields[target];
      fields[target] = {
        params: engine.filters[0].toJSON(),
        cmsCount: engine.cmsCount,
        cmsFullCount: engine.cmsFullCount,
      };
    });
    lastPaintedFields.current = fields;
  }
  const frozenAttrs = formApi.rate ? lastPaintedAttrs.current : null;
  const frozenFields = formApi.rate ? lastPaintedFields.current : null;

  // Prototype-delegating read facade: `get` is a FormModel class method, so
  // shadowing it on an Object.create() child leaves every other member (and
  // identity-sensitive `.id`) reaching the real model through the chain.
  const model = useMemo(() => {
    if (!liveModel || !frozenAttrs) return liveModel;
    return Object.create(liveModel, {
      get: { value: (key: string) => frozenAttrs[key] },
    }) as FormModel;
  }, [liveModel, frozenAttrs]);

  // Same trick per engine. `paramValue` is a class method and `cmsCount` /
  // `cmsFullCount` are instance fields — an own property on the Object.create()
  // child shadows either. `toggleSmartList`, `deselectModel` and `config` fall
  // through to the real engine, so the Data Bar's own Smartlist and Reset keep
  // working on the live catalogue while the displayed params sit still.
  const catalogueFields = useMemo(() => {
    if (!frozenFields) return formApi.catalogueFields;
    const out: typeof formApi.catalogueFields = {};
    Object.keys(formApi.catalogueFields).forEach((target) => {
      const engine = formApi.catalogueFields[target];
      const snap = frozenFields[target];
      out[target] = snap
        ? (Object.create(engine, {
            paramValue: { value: (field: string) => snap.params[field] },
            cmsCount: { value: snap.cmsCount },
            cmsFullCount: { value: snap.cmsFullCount },
          }) as typeof engine)
        : engine;
    });
    return out;
  }, [formApi.catalogueFields, frozenFields]);

  // Everything except the displayed values stays live: subtabs, toggleSubtab
  // and every commit handler keep pointing at the real form and the real
  // catalogue engines.
  //
  // The freeze is on what the DIALOG writes, not on the Data Bar's own inputs.
  // Legacy draws that line for free — a field the user edits keeps its DOM
  // value because nothing repaints over it — but `BareInput` re-syncs from
  // `value` on blur (formKit.tsx:323-325) and the params selects are fully
  // controlled, so a frozen snapshot would silently revert an edit made here
  // while the dialog is open. Fold each committed value into the snapshot.
  // Read it back off the live model/engine rather than re-deriving it: that
  // reuses the commit helpers' own null/'None'/`_id` normalisation instead of
  // forking a second copy of it.
  const api =
    !frozenAttrs || !frozenFields || !liveModel
      ? formApi
      : {
          ...formApi,
          model,
          catalogueFields,
          commitModelField: (name: string, raw: string | null) => {
            formApi.commitModelField(name, raw);
            frozenAttrs[name] = liveModel.get(name);
          },
          commitModelCheckbox: (name: string, checked: boolean) => {
            formApi.commitModelCheckbox(name, checked);
            frozenAttrs[name] = liveModel.get(name);
          },
          commitParamsField: (targetField: string, name: string, raw: string | boolean) => {
            formApi.commitParamsField(targetField, name, raw);
            const engine = formApi.catalogueFields[targetField];
            const snap = frozenFields[targetField];
            if (engine && snap) snap.params = engine.filters[0].toJSON();
          },
        };

  // ---- cross-filter web (parity 320) ----
  useEffect(() => {
    // Reads the LIVE model on purpose: this keeps the form's catalogue filters
    // in step with what the dialog is doing, which is state, not paint.
    const model = liveModel;
    if (!model) return undefined;
    const filterEngine = formApi.catalogueFields.master_filter_id;
    const frameEngine = formApi.catalogueFields.master_frame_id;
    if (!filterEngine || !frameEngine) return undefined;

    const applyFilterSide = () => {
      filterEngine.resetFilter(
        {
          arrangement_id: model.get('arrangement_id'),
          bank_configuration_id: model.get('size_qty_id') || 'null',
          frame: model.get('has_frame') ? 'true' : undefined,
        },
        false,
        1
      );
    };
    const applyFrameSide = () => {
      let filterParams: Record<string, unknown> = {};
      try {
        filterParams = JSON.parse(String(model.get('filter_params') ?? '{}'));
      } catch {
        /* malformed params JSON keeps the previous frame filter */
      }
      frameEngine.resetFilter(
        {
          frame_loading_id: model.get('frame_loading_id'),
          filter_mfg_id: filterParams.mfg_id,
          filter_type_id: filterParams.type_id,
          filter_efficiency_id: filterParams.efficiency_id,
          filter_depth_nominal_id: filterParams.depth_nominal_id,
          filter_ul_class_id: filterParams.ul_class_id,
          filter_merv_id: filterParams.merv_id,
          filter_series_id: filterParams.series_id,
          filter_id: model.get('master_filter_id'),
        },
        false,
        1
      );
    };

    // Lift skipFilteredCountOnInit BEFORE the init pass — legacy
    // components/filter/view/form.js:143-146 lifts both flags on the line above
    // its two resetFilter calls, so those init passes are the ones that fetch
    // the filter/frame counts. The port had the two lines the other way round,
    // which meant refreshCounts() early-returned on the only init-time fetch
    // (catalogueField.ts:212) and cmsCount/cmsFullCount stayed undefined for the
    // life of the engine: the selection dialog then read them back through the
    // shim's `?? 0` getters as "0 / 0", painted the Frame tab red and greyed
    // SELECT (frameOk needs frameCount === 1). The ctor's own refreshCounts is
    // still skipped, so this is one count round-trip per engine, as in legacy.
    frameEngine.skipFilteredCountOnInit = false;
    filterEngine.skipFilteredCountOnInit = false;
    applyFrameSide();
    applyFilterSide();

    return model.onChange((changed) => {
      if (
        'arrangement_id' in changed ||
        'size_qty_id' in changed ||
        'has_frame' in changed
      )
        applyFilterSide();
      if ('frame_loading_id' in changed || 'filter_params' in changed)
        applyFrameSide();
    });
    // Keyed on the LIVE model, not the render facade — re-subscribing every
    // time the freeze flips would re-run the init pass and re-arm
    // skipFilteredCountOnInit, i.e. spend catalogue fetches on opening a dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    liveModel,
    formApi.catalogueFields.master_filter_id,
    formApi.catalogueFields.master_frame_id,
  ]);

  if (api.loading || !model) {
    return <div className="preloader dotted-preloader active" />;
  }
  const sub = (name: string) => !!api.subtabs[name];
  const arrangements = Array.isArray(model.get('arrangements'))
    ? (model.get('arrangements') as Array<{ id: number; name?: string }>)
    : [];

  return (
    <>
      <FormHeading
        title="Filter"
        api={api}
        wrapperClass="component-form-heading clearfix rating-mode"
        chrome={[
          {
            field: config.catalogueFields[0],
            visClass: 'vis-filter',
            searchResultClass: '',
          },
        ]}
        chromeWrapperClass="ahu-header-btns right hide"
        withRatingBlock
        ratingBlockDataName="filter"
        ratingBlockWrapperClass="ahu-header-btns filter-ahu-header-btns"
      />
      <form onKeyDown={handleTabToNext}>
        <ul className="inputs-group max-height-auto">
          <FormSubtab type="filter" cid={row.id} name="properties" label="Properties" labelHidden open={sub('properties')} onToggle={api.toggleSubtab} sublistClass=" non-clickable">
            <li>
              <div className="single-line">
                <MBareSelect api={api} name="arrangement_id" options={arrangements} extraClass="medium-input" />
                {!!model.get('master_filter_id') && Number(model.get('arrangement_id')) === 2 && (
                  <span className="non-clickable degree medium-input">
                    <span className="unit">deg</span>
                    <input
                      type="text"
                      className="non-clickable small-input"
                      value={String(model.get('v_bank_angle') ?? '')}
                      readOnly
                      onChange={() => undefined}
                    />
                  </span>
                )}
                <span className="label">Arrangement</span>
              </div>
            </li>
            <PSelect api={api} target="master_filter_id" name="mfg_id" label="Manufacturer" cmsPath="manufacturer" />
            <PSelect api={api} target="master_filter_id" name="type_id" label="Type" cmsPath="type" />
            <PSelect api={api} target="master_filter_id" name="series_id" label="Series" cmsPath="series" labelKey="series" />
            <li>
              <div className="single-line">
                <PBareSelect api={api} target="master_filter_id" name="merv_id" cmsPath="merv" labelKey="value" extraClass="medium-input" />
                <PBareSelect api={api} target="master_filter_id" name="efficiency_id" cmsPath="efficiency" labelKey="value" extraClass="medium-input" />
                <span className="label">
                  <span>MERV</span> | <span>Efficiency</span>
                </span>
              </div>
            </li>
            <PSelect api={api} target="master_filter_id" name="depth_nominal_id" label="Nominal Depth" unit="in" cmsPath="nominal_depth" labelKey="value" extraClass="long_unit" />
            {/* size_and_qty — single option mirroring model.size_qty_name
                (legacy filter_calculated_field; §49.2 guard: size_qty_name
                changes never save). */}
            <li>
              <div className="long_unit">
                <span className="unit"> </span>
                <span className="selection">
                  {model.get('size_qty_name') ? String(model.get('size_qty_name')) : null}
                </span>
                <select
                  name="size_and_qty_id"
                  className="filter_calculated_field model_field"
                  value={model.get('size_qty_name') ? 'current' : 'None'}
                  onChange={() => undefined}
                >
                  <option value="None" className="hide">
                    -- x --
                  </option>
                  {!!model.get('size_qty_name') && (
                    <option value="current">{String(model.get('size_qty_name'))}</option>
                  )}
                </select>
                <span className="label">
                  <span className="">Bank Configuration W x H</span>
                </span>
              </div>
            </li>
            <li>
              {/* form.tmpl:163-176 — this row is sized by INLINE styles, not by
                  its classes: `min-width: 200px` on both the selection mirror
                  and the select (which beats the `width: calc(32.5% - 4px)` of
                  `.filter-supplied-sets select.medium-input`, styles.css:3883,
                  and the `max-width: 110px` of `.selection:has(+
                  select.medium-input)`, :3094), and `margin-left: 5px` on the
                  Sets input — whose `xsmall-input` class is NOT the
                  `input.small-input` the row's margin rule (:3887) selects, so
                  nothing else supplies it. Dropping all three, plus the
                  collapsed space before the input (§JSX-WS), left the select
                  48px narrow and the Sets box 57px left of dev. */}
              <div className="filter-supplied-sets single-line">
                <MBareSelect api={api} name="supplied_by_id" cmsPath="supplied_by" extraClass="medium-input" selectionClass="medium-selection" selectionStyle={{ minWidth: '200px' }} selectStyle={{ minWidth: '200px' }} hideNone />{' '}
                <MBareInput api={api} name="qty_of_sets" formatter="integer" extraClass="xsmall-input" style={{ marginLeft: '5px' }} />{' '}
                <span className="label">
                  <span>Supplied By</span> | <span>Sets</span>
                </span>
              </div>
            </li>
          </FormSubtab>

          {/* Dev-sync J6 — filter form.tmpl −26: the two hidden EDB|EWB /
              LDB|LWB rows were DELETED by the season wave (the filter's
              season UI lives in the rating SELECTOR's new Performance tab
              instead — survey §A3b/§A3c). */}
          <FormSubtab type="filter" cid={row.id} name="performance" label="Performance" open={sub('performance')} onToggle={api.toggleSubtab} sublistClass=" non-clickable">
            <AirStreamFlow api={api} cms={cms} rowClass="single-line" />
            <PerformanceCommon api={api} typePrefix="filter" />
          </FormSubtab>

          <FormSubtab
            type="filter"
            cid={row.id}
            name="frame"
            label="Frame"
            open={sub('frame')}
            onToggle={api.toggleSubtab}
            liClass="group-with-include-checkbox"
            liId="frame"
            sublistClass={` non-clickable${model.get('has_frame') ? ' active-group ' : ''}`}
            headerChrome={
              <HeadingChrome
                api={api}
                spec={{
                  field: config.catalogueFields[1],
                  visClass: '',
                  searchResultClass: '',
                  countVisible: !!model.get('has_frame'),
                }}
                wrapperClass="ahu-header-btns right hide"
              />
            }
          >
            <li className="include-checkbox">
              <div>
                <div className="checkboxes-group">
                  <label>
                    <input
                      type="checkbox"
                      name="has_frame"
                      className="model_field"
                      checked={!!model.get('has_frame')}
                      onChange={(e) => api.commitModelCheckbox('has_frame', e.target.checked)}
                    />
                  </label>
                </div>
                <span className="label">Include Frame</span>
              </div>
            </li>
            <PSelect api={api} target="master_frame_id" name="frame_type_id" label="Type" cmsPath="frame.type" />
            <PSelect api={api} target="master_frame_id" name="mfg_id" label="Manufacturer" cmsPath="frame.manufacturer" />
            <PSelect api={api} target="master_frame_id" name="series_id" label="Series" cmsPath="frame.series" labelKey="series" />
            <PSelect api={api} target="master_frame_id" name="mtl_id" label="Material" cmsPath="frame.mtl_gauge" extraClass="model_field" />
          </FormSubtab>

          <FormSubtab type="filter" cid={row.id} name="pricing" label="Pricing" open={sub('pricing')} onToggle={api.toggleSubtab} sublistClass={` non-clickable${Number(model.get('supplied_by_id')) !== 1 ? ' non-clickable' : ''}`}>
            <PricingRows api={api} label="Filter" />
          </FormSubtab>

          {/* §49.2 guard exception: blank_off gauge edits save even without a
              master pick — gauge input lives with the blank-off rows. */}
          <BlankOffSlot api={api} sectionId={section.id} />
          <InputRow
            name="blank_off_mtl_gauge_id"
            fieldClass="model_field hide"
            value={model.get('blank_off_mtl_gauge_id')}
            label=""
            formatter="integer"
            onCommit={(v) => api.commitModelField('blank_off_mtl_gauge_id', v)}
          />
        </ul>
      </form>
      <RequiredFieldNote />
    </>
  );
};

export default FilterForm;
